import { ModalV2 } from "#src/commonV2/modal/ModalV2";
import { useTranslation } from "react-i18next";
import { fileSensitivityItems } from "./filterItems";
import { ButtonV2 } from "#src/commonV2/Button";
import { FormTags } from "#src/commonV2/form/Tags";
import { useEffect, useMemo, useState } from "react";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import { endpointService } from "#src/services/endpoint";
import { IFileItem } from "./types";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { SensitivityEnum } from "#src/components/endpoint/dlp/dataLabeling/Sensitivity";
import { createToast } from "#src/common/system/toasts";
import { useFormDropdown } from "#src/commonV2/form/Dropdown/useFormDropdown";
import { FormDropdown } from "#src/commonV2/form/Dropdown/FormDropdown";

interface IEndpointDriveLabelingModalProps {
  workspaceId: string;
  deviceId: string;
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
  contentTagList: { id: number; name: string }[];
  selectedFileItem: IFileItem | null;
}

export const EndpointDriveLabelingModal = ({
  workspaceId,
  deviceId,
  open,
  onClose,
  onRefresh,
  contentTagList,
  selectedFileItem,
}: IEndpointDriveLabelingModalProps) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "devices.drive.modal.labelDocument",
  });

  // --------------- INITIALIZATION ---------------

  const defaultSensitivity = useMemo(
    () =>
      fileSensitivityItems.find(
        (item) =>
          selectedFileItem &&
          selectedFileItem.sensitivity.length > 0 &&
          item.value === selectedFileItem.sensitivity[0].id
      ) ?? fileSensitivityItems[4],
    [selectedFileItem]
  );

  const defaultContentTag = useMemo(() => {
    const contentIds =
      selectedFileItem === null
        ? []
        : selectedFileItem.content.map((item) => item.id.toString());

    return contentTagList
      .filter((tag) => contentIds.includes(tag.id.toString()))
      .map((item) => ({
        key: item.id.toString(),
        value: item.id.toString(),
        getLabel: () => item.name,
      }));
  }, [selectedFileItem, contentTagList]);

  const {
    selected: sensitivity,
    reset: resetSensitivityField,
    renderProps: sensitivityFieldRenderProps,
  } = useFormDropdown({
    label: t("bySensitivity.label"),
    defaultSelected: defaultSensitivity,
    options: fileSensitivityItems,
  });
  const [selectedTags, setSelectedTags] =
    useState<IFilterItemV2[]>(defaultContentTag);
  const [tagKeyword, setTagKeyword] = useState<string>("");

  // --------------- METHODS ---------------

  const onSubmit = () => {
    console.log(sensitivity);
    if (selectedFileItem === null) return;

    endpointService
      .updateFileLabels(
        workspaceId,
        deviceId,
        selectedFileItem.id,
        sensitivity.value ?? SensitivityEnum.NOT_APPLICABLE,
        selectedTags
          .filter((item) => item.value !== undefined)
          .map((item) => Number(item.value))
      )
      .then((response) => {
        console.log(response);
        createToast({
          type: "success",
          message: t("notification.success.message"),
          detail: t("notification.success.detail"),
        });
        onRefresh();
        onCloseAndCleanup();
      })
      .catch(apiErrorHandler);
  };

  // --------------- COMPUTED ---------------

  const onCloseAndCleanup = () => {
    resetSensitivityField();
    setSelectedTags(defaultContentTag);
    setTagKeyword("");
    onClose();
  };

  // --------------- EFFECTS ---------------

  useEffect(() => {
    setSelectedTags(defaultContentTag);
  }, [defaultContentTag]);

  // --------------- RENDER ---------------

  return (
    <ModalV2 open={open} onChangeOpen={onCloseAndCleanup} className="w-[44rem]">
      <span className="text-lg font-semibold text-gray-v2-900">
        {t("title")}
      </span>
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-6">
          <FormDropdown {...sensitivityFieldRenderProps} />
          <FormTags
            label={t("byContent.label")}
            values={selectedTags}
            onChangeValues={(newValues) => {
              setSelectedTags(newValues);
            }}
            keyword={tagKeyword}
            onChangeKeyword={(newKeyword) => {
              setTagKeyword(newKeyword);
            }}
            options={
              contentTagList
                ? contentTagList.map((item) => ({
                    key: item.id.toString(),
                    value: item.id.toString(),
                    getLabel: () => item.name,
                  }))
                : []
            }
            placeholder={t("byContent.placeholder")}
          />
        </div>
        <div className="flex justify-end gap-2">
          <ButtonV2
            variant="secondary"
            color="gray"
            onClick={onCloseAndCleanup}
          >
            {t("button.cancel")}
          </ButtonV2>
          <ButtonV2 onClick={onSubmit}>{t("button.submit")}</ButtonV2>
        </div>
      </div>
    </ModalV2>
  );
};
